/**
 * TerrainDisplay.ts
 * -----------------------------------------------------------------------------
 * How ground is shaded and tessellated, and the one implementation of it that
 * both routes to a terrain use.
 *
 * There are two, and they arrive very differently. Map/GIS ▸ Import Map parses a
 * LandXML file in the browser (io/LandXmlScene.ts) and has the TIN in hand. The
 * Modeler gets its ground as part of the ordinary bridge payload, exported from
 * C# out of `xApp.Terrain` — no TIN, no elevations, just triangles that happen to
 * be ground, flagged `terrain: true` by BridgeGeometryExporter.
 *
 * So the shaping works on the one thing both have: a flat array of viewer-space
 * triangle vertices. Keeping it here rather than in either caller is what stops
 * the two screens drifting into different-looking terrain.
 */

import * as THREE from 'three';
import { LoopSubdivision } from 'three-subdivide';

/** How the ground is drawn. */
export interface TerrainDisplay {
  /** Shade by height instead of one flat tone. */
  elevation: boolean;
  /** Draw every triangle edge, not just the shaded mesh. */
  wireframe: boolean;
  /** Loop subdivision passes, 0 (off) to 3. */
  subdivide: number;
  /**
   * Smooth to the Loop limit surface rather than only tessellating.
   *
   * Off is pure tessellation: the new vertices land on the plane of the parent
   * triangle, so the surface is unchanged and only the shading gets finer. On,
   * the vertices move — which is what makes coarse ground read as smooth, and
   * also means the drawn surface is no longer the delivered triangulation.
   */
  smooth: boolean;
  /**
   * Trees and grass scattered over the ground, 0 (none) to 1 (as thick as it
   * goes). Scenery only — see io/Vegetation.ts; it is never part of the model.
   */
  vegetation: number;
}

/**
 * How ground is drawn before anyone touches the toolbar: shaded by height, its
 * mesh shown, and smoothed twice.
 *
 * Restated in the two hosts' own field initialisers (MapImportView.razor and
 * Modeler.razor.cs) — the buttons have to light up for the state the scene is
 * actually in, and the host owns the buttons. Change this and change those.
 */
export const DEFAULT_TERRAIN: TerrainDisplay = {
  elevation: true,
  wireframe: true,
  subdivide: 2,
  smooth: true,
  vegetation: 0,
};

/**
 * Elevation ramp: low water-blue through teal to high green, as ordinary
 * hypsometric tinting reads. Five stops rather than two — a straight blue-to-green
 * interpolation passes through a muddy grey-cyan in the middle third and loses all
 * the relief there, which is exactly the part of a site you are usually reading.
 *
 * RGB 0..255, so the same array can be handed to the legend for its CSS gradient
 * and the two cannot drift apart.
 */
export const ELEVATION_STOPS: ReadonlyArray<readonly [number, number, number]> = [
  [31, 78, 140],    // low
  [43, 123, 176],
  [63, 163, 163],
  [92, 191, 122],
  [143, 209, 79],   // high
] as const;

/**
 * The same ramp in earth tones, used instead of the green one once the ground is
 * planted. Green grass over green ground is mush — the relief that the elevation
 * shading exists to show disappears into the vegetation — so the ground goes to
 * soil and the planting supplies the green.
 *
 * Still a RANGE rather than one flat brown: the legend and the shading come off
 * whichever of these two arrays is in use, so switching to earth keeps the height
 * readable instead of trading the feature away for a colour.
 */
export const EARTH_STOPS: ReadonlyArray<readonly [number, number, number]> = [
  [74, 52, 38],     // low
  [104, 74, 50],
  [134, 101, 66],
  [164, 132, 90],
  [193, 166, 122],  // high
] as const;

/** Flat soil tone, for planted ground with elevation shading switched off. */
export const EARTH_COLOR = '#8a6a4d';

/** Which ramp applies, given whether anything is planted on the ground. */
export function rampFor(vegetated: boolean): ReadonlyArray<readonly [number, number, number]> {
  return vegetated ? EARTH_STOPS : ELEVATION_STOPS;
}

/** Colour of the triangle-edge overlay. Near the ground's own tone, so the mesh
 *  reads as a wireframe over terrain rather than as a second object. */
export const WIREFRAME_COLOR = '#4a5560';

/**
 * Subdivision is quadratic in memory and the result is a non-indexed mesh, so a
 * survey-sized TIN has to be stopped before it takes the tab down.
 *
 * The budget is for ALL the ground at once, not each surface. A corridor export
 * routinely carries three (existing ground, datum, finished top), so a per-surface
 * ceiling is really three times what it says: Corridor-6a at 3 passes came to
 * 512,000 faces and 771,000 wireframe segments, and took four seconds to build.
 * One pass count is then used for every surface, so they also stay consistent with
 * each other rather than degrading unevenly as a shared budget ran out.
 */
export const MAX_SUBDIVIDED_FACES = 250_000;

/** Passes the face budget allows, counting the RESULT of each — the modifier
 *  tests the count before a pass, so the budget it is handed is a quarter of the
 *  ceiling. */
export function affordableSubdivisions(faces: number, wanted: number): number {
  let n = 0;
  let f = faces;
  while (n < wanted && f * 4 <= MAX_SUBDIVIDED_FACES) { f *= 4; n++; }
  return n;
}

/** Clamp a requested pass count to what the option actually allows. */
export function wantedPasses(subdivide: number): number {
  return Math.max(0, Math.min(3, subdivide | 0));
}

/** Colour at 0..1 along a ramp, as 0..1 sRGB. */
export function elevationAt(
  t: number, stops: ReadonlyArray<readonly [number, number, number]> = ELEVATION_STOPS,
): [number, number, number] {
  const u = Math.min(1, Math.max(0, t)) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(u));
  const f = u - i;
  const a = stops[i], b = stops[i + 1];
  return [
    (a[0] + (b[0] - a[0]) * f) / 255,
    (a[1] + (b[1] - a[1]) * f) / 255,
    (a[2] + (b[2] - a[2]) * f) / 255,
  ];
}

export interface ShapedTerrain {
  /** Triangle vertices after any subdivision. */
  positions: Float32Array;
  /** Per-vertex ramp colour, or undefined when elevation shading is off. */
  vcolors?: number[];
  /** De-duplicated triangle edges, or undefined when the wireframe is off. */
  edges?: Float32Array;
}

/**
 * Subdivide, shade and outline one patch of ground.
 *
 * `positions` is viewer-space triangle soup (metres, Y-up); `loY`/`hiY` are the
 * height range the ramp is stretched over — passed in rather than measured here,
 * so every patch in a scene is coloured against the same scale and two surfaces
 * at different heights stay comparable.
 */
export function shapeTerrain(
  positions: Float32Array, passes: number, opts: TerrainDisplay,
  loY: number, hiY: number,
): ShapedTerrain {
  let out = positions;
  let geom: THREE.BufferGeometry | null = null;

  // A geometry is needed for subdivision, and WireframeGeometry needs one too.
  if (passes > 0 || opts.wireframe) {
    geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  }

  if (geom && passes > 0) {
    const subdivided = LoopSubdivision.modify(geom, passes, {
      // A TIN has no coplanar quads to presplit, and the presplit pass is the
      // expensive one.
      split: false,
      uvSmooth: false,
      // Hold the outline still; without this the ground shrinks away from its
      // own boundary on every pass.
      preserveEdges: true,
      flatOnly: !opts.smooth,
      // The modifier tests the count BEFORE a pass, so it is given a quarter of
      // the ceiling. `passes` already respects this; the cap is the backstop.
      maxTriangles: MAX_SUBDIVIDED_FACES / 4,
    });
    if (subdivided !== geom) geom.dispose();
    geom = subdivided;
    out = geom.getAttribute('position').array as Float32Array;
  }

  // Shaded from the vertices actually being drawn, so a subdivided surface
  // colours its new vertices by where they ended up rather than inheriting the
  // parent triangle's tone.
  let vcolors: number[] | undefined;
  const span = hiY - loY;
  if (opts.elevation && span > 1e-9) {
    const stops = rampFor(opts.vegetation > 0);
    const n = out.length / 3;
    vcolors = new Array<number>(n * 3);
    for (let v = 0; v < n; v++) {
      const c = elevationAt((out[v * 3 + 1] - loY) / span, stops);
      vcolors[v * 3] = c[0];
      vcolors[v * 3 + 1] = c[1];
      vcolors[v * 3 + 2] = c[2];
    }
  }

  // Edges follow whatever is actually drawn, so the wireframe shows the
  // subdivided faces rather than the mesh they came from. WireframeGeometry
  // de-duplicates shared edges, which matters because the mesh is non-indexed
  // and every interior edge is therefore stored twice.
  let edges: Float32Array | undefined;
  if (geom && opts.wireframe) {
    const wf = new THREE.WireframeGeometry(geom);
    const e = wf.getAttribute('position').array as Float32Array;
    if (e.length >= 6) edges = e;
    wf.dispose();
  }

  geom?.dispose();
  return { positions: out, vcolors, edges };
}
